import React, { useState } from "react";
import {
  Box,
  Button,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  Icon,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Stack,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  IconButton,
  useToast,
} from "@chakra-ui/react";
import {
  FaPlus,
  FaTrash,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaListAlt,
} from "react-icons/fa";
import { BASE_URL } from "../../../Globalapi/Globalapi";

const Fine_Modal = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fineAmount, setFineAmount] = useState(""); // Fixed fine amount
  const [dueDate, setDueDate] = useState(""); // Fine start date
  const [intervals, setIntervals] = useState([]); // Initially empty intervals
  const [fineType, setFineType] = useState("fixed"); // Track if fine is fixed or interval
  const toast = useToast();

  const API_URL = `${BASE_URL}/admin/bus/fee/create_fee_structure.php`;

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const handleFineAmountChange = (e) => setFineAmount(e.target.value);
  const handleDueDateChange = (e) => setDueDate(e.target.value);

  const handleIntervalChange = (index, field, value) => {
    const updatedIntervals = [...intervals];
    updatedIntervals[index][field] = value;
    setIntervals(updatedIntervals);
  };

  const addNewInterval = () => {
    setIntervals([...intervals, { days: "", fineAmount: "" }]);
  };

  const deleteInterval = (index) => {
    const updatedIntervals = intervals.filter((_, i) => i !== index);
    setIntervals(updatedIntervals);
  };

  const handleSubmit = async () => {
    const fineIntervals = intervals.map((interval) => ({
      days: interval.days,
      fine_amount: interval.fineAmount,
    }));

    const requestData = {
      user_id: sessionStorage.getItem("user_id"), // Replace with actual user ID
      school_id: sessionStorage.getItem("school_id"), // Replace with actual school ID
      fine_type: fineType, // "fixed" or "interval"
      fixed_fine_amount: fineAmount, // Fixed fine amount (only used for fixed type)
      fine_intervals: fineIntervals, // Intervals for fine amounts (only used for interval type)
    };
    console.log("req create", requestData);

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        toast({
          title: "Success",
          description: "Bus fine created successfully!",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        closeModal();
      } else {
        toast({
          title: "Error",
          description: result.message || "Failed to create bus fine.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occurred while submitting the fine details.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Box>
      <Button
        leftIcon={<FaPlus />}
        marginRight={2}
        backgroundColor="red.500"
        color="white"
        fontSize="14px"
        size={{ base: "xs", md: "md" }}
        boxShadow="md"
        width="auto"
        padding="10px 20px"
        onClick={openModal}
      >
        Fine
      </Button>

      <Modal
        isCentered
        isOpen={isModalOpen}
        onClose={closeModal}
        size="lg"
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent
          borderRadius="lg"
          boxShadow="xl"
          bg="linear-gradient(135deg, #e0f7f7, #f0ffff)"
          color="gray.700"
          p={6}
          maxW="700px"
          maxH="550px"
          overflowY="auto"
        >
          <ModalHeader
            fontSize="xl"
            fontWeight="bold"
            textAlign="center"
            color="teal.600"
          >
            <Icon as={FaPlus} boxSize={5} mr={2} />
            Fine Details
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            overflowY="auto"
            px={4}
            css={{
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(173, 216, 230, 0.6)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "rgba(173, 216, 230, 0.9)",
              },
            }}
          >
            <Stack spacing={4}>
              {/* Fine Type Selection */}
              <FormControl>
                <FormLabel>
                  {" "}
                  <Icon as={FaListAlt} mr={1} color="teal.500" />
                  Fine Type
                </FormLabel>
                <RadioGroup
                  value={fineType}
                  onChange={setFineType}
                  defaultValue="fixed"
                >
                  <Stack direction="row">
                    <Radio value="fixed">Fixed</Radio>
                    <Radio value="interval">Interval</Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>

              {/* Fixed Fine Fields */}
              {fineType === "fixed" && (
                <>
                  <FormControl>
                    <FormLabel>
                      {" "}
                      <Icon as={FaMoneyBillWave} mr={1} color="teal.500" />
                      Fine Amount
                    </FormLabel>
                    <Input
                      bg="white"
                      _hover={{ borderColor: "teal.300" }}
                      type="number"
                      value={fineAmount}
                      onChange={handleFineAmountChange}
                      placeholder="Enter fixed fine amount"
                    />
                  </FormControl>

                  {/* <FormControl>
                    <FormLabel>Due Date</FormLabel>
                    <Input
                      type="date"
                      value={dueDate}
                      onChange={handleDueDateChange}
                    />
                  </FormControl> */}
                </>
              )}

              {/* Interval Fine Fields */}
              {fineType === "interval" && (
                <>
                  {intervals.map((interval, index) => (
                    <Box key={index} mb={2}>
                      <Stack direction="row" spacing={4}>
                        <FormControl>
                          <FormLabel>
                            {" "}
                            <Icon as={FaCalendarAlt} mr={1} color="teal.500" />
                            Days
                          </FormLabel>
                          <Input
                            bg="white"
                            _hover={{ borderColor: "teal.300" }}
                            type="number"
                            value={interval.days}
                            onChange={(e) =>
                              handleIntervalChange(
                                index,
                                "days",
                                e.target.value
                              )
                            }
                            placeholder="Days"
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel>
                            {" "}
                            <Icon
                              as={FaMoneyBillWave}
                              mr={1}
                              color="teal.500"
                            />
                            Fine Amount
                          </FormLabel>
                          <Input
                            bg="white"
                            _hover={{ borderColor: "teal.300" }}
                            type="number"
                            value={interval.fineAmount}
                            onChange={(e) =>
                              handleIntervalChange(
                                index,
                                "fineAmount",
                                e.target.value
                              )
                            }
                            placeholder="Fine Amount"
                          />
                        </FormControl>

                        <IconButton
                          icon={<FaTrash />}
                          marginTop={9}
                          colorScheme="red"
                          onClick={() => deleteInterval(index)}
                          aria-label="Delete interval"
                          size="sm"
                        />
                      </Stack>
                    </Box>
                  ))}

                  <Button
                    leftIcon={<FaPlus />}
                    colorScheme="teal"
                    onClick={addNewInterval}
                    size="sm"
                    borderColor="teal"
                    variant="outline"
                  >
                    Add Interval
                  </Button>
                </>
              )}
            </Stack>
          </ModalBody>

          <ModalFooter>
            {/* <Button variant="ghost" onClick={closeModal}>
              Cancel
            </Button> */}
            <Button
              bgGradient="linear(to-r, teal.400, teal.600)"
              color="white"
              _hover={{ bgGradient: "linear(to-r, teal.500, teal.700)" }}
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Fine_Modal;
